"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState, type ReactNode } from "react";
import type { ButtonProps as AriaButtonProps, TooltipProps as AriaTooltipProps, TooltipTriggerComponentProps as AriaTooltipTriggerComponentProps } from "react-aria-components";
import { Button as AriaButton, DialogTrigger as AriaDialogTrigger, OverlayArrow as AriaOverlayArrow, Popover as AriaPopover, Tooltip as AriaTooltip, TooltipTrigger as AriaTooltipTrigger } from "react-aria-components";
import { usePrefersTapTooltips } from "@/hooks/use-prefers-tap-tooltips";
import { cx } from "@/utils/cx";
interface TooltipProps extends AriaTooltipTriggerComponentProps, Omit<AriaTooltipProps, "children"> {
  /**
   * The title of the tooltip.
   */
  title: ReactNode;
  /**
   * The description of the tooltip.
   */
  description?: ReactNode;
  /**
   * Whether to show the arrow on the tooltip.
   *
   * @default false
   */
  arrow?: boolean;
  /**
   * Delay in milliseconds before the tooltip is shown.
   *
   * @default 300
   */
  delay?: number;
}
function getCrossOffset(placement: TooltipProps["placement"]) {
  const isTopOrBottomLeft = ["top left", "top end", "bottom left", "bottom end"].includes(placement ?? "top");
  const isTopOrBottomRight = ["top right", "top start", "bottom right", "bottom start"].includes(placement ?? "top");
  return isTopOrBottomLeft ? -12 : isTopOrBottomRight ? 12 : 0;
}
function TooltipBody(t0) {
  const $ = _c(15);
  const {
    title,
    description,
    arrow,
    isEntering,
    isExiting
  } = t0;
  const t1 = description ? "w-80 max-w-[min(20rem,calc(100vw-1.5rem))] py-3" : "w-max max-w-xs py-2";
  const t2 = isEntering && "ease-out animate-in fade-in in-placement-left:slide-in-from-right-0.5 in-placement-right:slide-in-from-left-0.5 in-placement-top:slide-in-from-bottom-0.5 in-placement-bottom:slide-in-from-top-0.5";
  const t3 = isExiting && "ease-out animate-out fade-out in-placement-left:slide-out-to-right-0.5 in-placement-right:slide-out-to-left-0.5 in-placement-top:slide-out-to-bottom-0.5 in-placement-bottom:slide-out-to-top-0.5";
  let t4;
  if ($[0] !== t1 || $[1] !== t2 || $[2] !== t3) {
    t4 = cx("z-50 flex min-w-0 origin-(--trigger-anchor-point) flex-col items-stretch gap-1 rounded-lg bg-primary-solid px-3 shadow-lg will-change-transform", t1, t2, t3);
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== title) {
    t5 = <span className="text-xs font-semibold text-white">{title}</span>;
    $[4] = title;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== description) {
    t6 = description && <span className="w-full text-xs font-medium text-pretty text-tooltip-supporting-text">{description}</span>;
    $[6] = description;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== arrow) {
    t7 = arrow && <AriaOverlayArrow><svg viewBox="0 0 100 100" className="size-2.5 fill-bg-primary-solid in-placement-left:-rotate-90 in-placement-right:rotate-90 in-placement-top:rotate-0 in-placement-bottom:rotate-180"><path d="M0,0 L35.858,35.858 Q50,50 64.142,35.858 L100,0 Z" /></svg></AriaOverlayArrow>;
    $[8] = arrow;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t4 || $[11] !== t5 || $[12] !== t6 || $[13] !== t7) {
    t8 = <div className={t4}>{t5}{t6}{t7}</div>;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  return t8;
}
function HoverTooltip(t0) {
  const $ = _c(43);
  let children;
  let crossOffset;
  let defaultOpen;
  let description;
  let isDisabled;
  let isOpen;
  let onOpenChange;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let title;
  let tooltipProps;
  let trigger;
  if ($[0] !== t0) {
    ({
      title,
      description,
      children,
      arrow: t1,
      delay: t2,
      closeDelay: t3,
      trigger,
      isDisabled,
      isOpen,
      defaultOpen,
      offset: t4,
      crossOffset,
      placement: t5,
      onOpenChange,
      ...tooltipProps
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = crossOffset;
    $[3] = defaultOpen;
    $[4] = description;
    $[5] = isDisabled;
    $[6] = isOpen;
    $[7] = onOpenChange;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = title;
    $[14] = tooltipProps;
    $[15] = trigger;
  } else {
    children = $[1];
    crossOffset = $[2];
    defaultOpen = $[3];
    description = $[4];
    isDisabled = $[5];
    isOpen = $[6];
    onOpenChange = $[7];
    t1 = $[8];
    t2 = $[9];
    t3 = $[10];
    t4 = $[11];
    t5 = $[12];
    title = $[13];
    tooltipProps = $[14];
    trigger = $[15];
  }
  const arrow = t1 === undefined ? false : t1;
  const delay = t2 === undefined ? 300 : t2;
  const closeDelay = t3 === undefined ? 0 : t3;
  const offset = t4 === undefined ? 6 : t4;
  const placement = t5 === undefined ? "top" : t5;
  let t6;
  if ($[16] !== placement) {
    t6 = getCrossOffset(placement);
    $[16] = placement;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const calculatedCrossOffset = t6;
  let t7;
  if ($[18] !== closeDelay || $[19] !== defaultOpen || $[20] !== delay || $[21] !== isDisabled || $[22] !== isOpen || $[23] !== onOpenChange || $[24] !== trigger) {
    t7 = {
      trigger,
      delay,
      closeDelay,
      isDisabled,
      isOpen,
      defaultOpen,
      onOpenChange
    };
    $[18] = closeDelay;
    $[19] = defaultOpen;
    $[20] = delay;
    $[21] = isDisabled;
    $[22] = isOpen;
    $[23] = onOpenChange;
    $[24] = trigger;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  const t8 = crossOffset ?? calculatedCrossOffset;
  let t9;
  if ($[26] !== description) {
    t9 = t10 => {
      const {
        isEntering,
        isExiting
      } = t10;
      return cx(description && "w-80 max-w-[min(20rem,calc(100vw-1.5rem))]", isEntering && "ease-out animate-in", isExiting && "ease-out animate-out");
    };
    $[26] = description;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== arrow || $[29] !== description || $[30] !== title) {
    t10 = t11 => {
      const {
        isEntering: isEntering_0,
        isExiting: isExiting_0
      } = t11;
      return <TooltipBody title={title} description={description} arrow={arrow} isEntering={isEntering_0} isExiting={isExiting_0} />;
    };
    $[28] = arrow;
    $[29] = description;
    $[30] = title;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  let t11;
  if ($[32] !== offset || $[33] !== placement || $[34] !== t10 || $[35] !== t8 || $[36] !== t9 || $[37] !== tooltipProps) {
    t11 = <AriaTooltip {...tooltipProps} offset={offset} placement={placement} crossOffset={t8} className={t9}>{t10}</AriaTooltip>;
    $[32] = offset;
    $[33] = placement;
    $[34] = t10;
    $[35] = t8;
    $[36] = t9;
    $[37] = tooltipProps;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  let t12;
  if ($[39] !== children || $[40] !== t11 || $[41] !== t7) {
    t12 = <AriaTooltipTrigger {...t7}>{children}{t11}</AriaTooltipTrigger>;
    $[39] = children;
    $[40] = t11;
    $[41] = t7;
    $[42] = t12;
  } else {
    t12 = $[42];
  }
  return t12;
}
function TapTooltip(t0) {
  const $ = _c(23);
  const {
    title,
    description,
    children,
    arrow: t1,
    isDisabled,
    isOpen,
    defaultOpen,
    offset: t2,
    crossOffset,
    placement: t3,
    onOpenChange
  } = t0;
  const arrow = t1 === undefined ? false : t1;
  const offset = t2 === undefined ? 6 : t2;
  const placement = t3 === undefined ? "top" : t3;
  let t4;
  if ($[0] !== placement) {
    t4 = getCrossOffset(placement);
    $[0] = placement;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const calculatedCrossOffset = t4;
  let t5;
  if ($[2] !== defaultOpen || $[3] !== isDisabled || $[4] !== isOpen || $[5] !== onOpenChange) {
    t5 = {
      isDisabled,
      isOpen,
      defaultOpen,
      onOpenChange
    };
    $[2] = defaultOpen;
    $[3] = isDisabled;
    $[4] = isOpen;
    $[5] = onOpenChange;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const t6 = crossOffset ?? calculatedCrossOffset;
  let t7;
  if ($[7] !== description) {
    t7 = t8 => {
      const {
        isEntering,
        isExiting
      } = t8;
      return cx("outline-hidden", description && "w-80 max-w-[min(20rem,calc(100vw-1.5rem))]", isEntering && "ease-out animate-in", isExiting && "ease-out animate-out");
    };
    $[7] = description;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== arrow || $[10] !== description || $[11] !== title) {
    t8 = t9 => {
      const {
        isEntering: isEntering_0,
        isExiting: isExiting_0
      } = t9;
      return <TooltipBody title={title} description={description} arrow={arrow} isEntering={isEntering_0} isExiting={isExiting_0} />;
    };
    $[9] = arrow;
    $[10] = description;
    $[11] = title;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== offset || $[14] !== placement || $[15] !== t6 || $[16] !== t7 || $[17] !== t8) {
    t9 = <AriaPopover offset={offset} placement={placement} crossOffset={t6} className={t7}>{t8}</AriaPopover>;
    $[13] = offset;
    $[14] = placement;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== children || $[20] !== t5 || $[21] !== t9) {
    t10 = <AriaDialogTrigger {...t5}>{children}{t9}</AriaDialogTrigger>;
    $[19] = children;
    $[20] = t5;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  return t10;
}
export const Tooltip = props => {
  const $ = _c(6);
  const prefersTapTooltips = usePrefersTapTooltips();
  const [mounted, setMounted] = useState(false);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      setMounted(true);
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  if (mounted && prefersTapTooltips) {
    let t2;
    if ($[2] !== props) {
      t2 = <TapTooltip {...props} />;
      $[2] = props;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  let t2;
  if ($[4] !== props) {
    t2 = <HoverTooltip {...props} />;
    $[4] = props;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};
interface TooltipTriggerProps extends AriaButtonProps {}
export const TooltipTrigger = t0 => {
  const $ = _c(10);
  let buttonProps;
  let children;
  let className;
  if ($[0] !== t0) {
    ({
      children,
      className,
      ...buttonProps
    } = t0);
    $[0] = t0;
    $[1] = buttonProps;
    $[2] = children;
    $[3] = className;
  } else {
    buttonProps = $[1];
    children = $[2];
    className = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = values => cx("h-max w-max outline-hidden", typeof className === "function" ? className(values) : className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== buttonProps || $[7] !== children || $[8] !== t1) {
    t2 = <AriaButton {...buttonProps} className={t1}>{children}</AriaButton>;
    $[6] = buttonProps;
    $[7] = children;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};